import React from "react";
import { structuredValue, isTechnicalFailed } from "../utils/tablePresentation";
import api from "../api/client";
import {
  DateRangePicker,
  usePagination,
  Pager,
  ModalShell,
} from "../components/ui-helpers";
import PageHeader from "../components/PageHeader";
import { formatDateTime } from "../utils/datetime";
import { downloadBlob } from "../utils/download";
import { useTerminology } from "../context/TerminologyContext";
import { useAuth } from "../context/AuthContext";
import { useOrg } from "../context/OrgContext";


function parseDetails(value) {
  if (!value) return null;
  if (typeof value === "object") return value;
  try {
    const parsed = JSON.parse(String(value));
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (_) {
    return null;
  }
}

function hasDetails(value) {
  const parsed = parseDetails(value);
  return !!parsed && Object.keys(parsed).length > 0;
}

function blankDash(value) {
  return structuredValue(value);
}

function splitDetails(value) {
  const parsed = parseDetails(value);
  if (!parsed) return { target: null, changes: null, flat: null };
  const hasStructured =
    Object.prototype.hasOwnProperty.call(parsed, "target") ||
    Object.prototype.hasOwnProperty.call(parsed, "changes");
  if (!hasStructured) return { target: null, changes: parsed, flat: parsed };
  const target = parsed.target && typeof parsed.target === "object" ? parsed.target : null;
  const changes = parsed.changes && typeof parsed.changes === "object" ? parsed.changes : {};
  return { target, changes, flat: null };
}

function targetEntries(target) {
  if (!target || typeof target !== "object") return [];
  return Object.entries(target).filter(([, value]) => value !== null && value !== undefined && String(value).trim() !== "");
}

function formatDetailValue(value) {
  if (value === null || value === undefined || value === "") return "—";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  return String(value);
}

const detailCellStyle = {
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  overflowWrap: "anywhere",
  maxWidth: 360,
};

function displayActor(value) {
  return String(value || "")
    .trim()
    .toLowerCase() === "queue_worker"
    ? "System"
    : structuredValue(value);
}

export default function AuditTrailPage() {
  const { t, tp } = useTerminology();
  const { current: currentOrg } = useOrg();
  const { user } = useAuth();
  const isSuperAdmin = user?.currentRole === "Super Admin";
  const [rawRows, setRows] = React.useState([]);
  const rows = React.useMemo(() => isSuperAdmin ? rawRows : rawRows.filter((row) =>
    (!isTechnicalFailed(row.action) || row.failure_kind === "confirmed") && String(row.actor || "").trim().toLowerCase() !== "super admin"
  ), [rawRows, isSuperAdmin]);
  const [q, setQ] = React.useState("");
  const [sortBy, setSortBy] = React.useState("time");
  const [order, setOrder] = React.useState("desc");
  const [typeFilter, setTypeFilter] = React.useState("");
  const [actionFilter, setActionFilter] = React.useState("");
  const [dateDraft, setDateDraft] = React.useState({
    date_from: "",
    date_to: "",
  });
  const [dateRange, setDateRange] = React.useState({
    date_from: "",
    date_to: "",
  });
  const [rawDetailsRow, setDetailsRow] = React.useState(null);
  const detailsRow = rawDetailsRow && (isSuperAdmin || ((!isTechnicalFailed(rawDetailsRow.action) || rawDetailsRow.failure_kind === "confirmed") && String(rawDetailsRow.actor || "").trim().toLowerCase() !== "super admin")) ? rawDetailsRow : null;
  const [exportError, setExportError] = React.useState("");
  const reloadSerial = React.useRef(0);


  const reload = React.useCallback(() => {
    if (!currentOrg) return;
    const serial = ++reloadSerial.current;
    api
      .get("/audit/list", {
        params: {
          q,
          sort_by: sortBy,
          order,
          type: typeFilter || undefined,
          action: actionFilter || undefined,
          date_from: dateRange.date_from || undefined,
          date_to: dateRange.date_to || undefined,
        },
      })
      .then((r) => {
        if (serial === reloadSerial.current) setRows(r.data.rows || []);
      })
      .catch(() => {
        if (serial === reloadSerial.current) setRows([]);
      });
  }, [q, sortBy, order, typeFilter, actionFilter, dateRange, currentOrg]);
  React.useEffect(() => {
    reload();
  }, [reload]);


  const actionOptions = React.useMemo(() => {
    const values = new Set(rows.map((r) => r.action).filter(Boolean));
    if (actionFilter && (isSuperAdmin || !isTechnicalFailed(actionFilter) || rows.some((r) => r.action === actionFilter && r.failure_kind === "confirmed"))) values.add(actionFilter);
    return Array.from(values).sort((a, b) => String(a).localeCompare(String(b)));
  }, [rows, actionFilter, isSuperAdmin]);

  const pg = usePagination(rows, 25);
  const { setPage: setAuditPage } = pg;

  React.useEffect(() => {
    setRows([]);
    setTypeFilter("");
    setActionFilter("");
    setDetailsRow(null);
    setExportError("");
    setAuditPage(1);
  }, [currentOrg, isSuperAdmin, setAuditPage]);

  const downloadExport = async (extension) => {
    setExportError("");
    try {
      const res = await api.get(`/export/audit.${extension}`, {
        params: {
          q,
          sort_by: sortBy,
          order,
          type: typeFilter || undefined,
          action: actionFilter || undefined,
          date_from: dateRange.date_from || undefined,
          date_to: dateRange.date_to || undefined,
        },
        responseType: "blob",
      });
      downloadBlob(`audit.${extension}`, res.data);
    } catch (_) {
      setExportError(`Could not download audit ${extension === "xlsx" ? "Excel" : "CSV"}.`);
    }
  };

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Operations"
        icon="audit"
        title="Audit Trail"
        subtitle="Review activity across uploads, reviews, approvals, with filtering."
      />
      <div className="section">
        <div className="filters-bar audit-toolbar">
          <label className="field-grow">
            <span>Search</span>
            <input
              placeholder={`Search actor, ${t("vendor").toLowerCase()}, ${t("material").toLowerCase()}, SKU, ${t("lot").toLowerCase()}, or action`}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </label>
          <label>
            <span>Sort by</span>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="time">Sort: Time</option>
              <option value="actor">Sort: Actor</option>
              <option value="vendor">Sort: {t("vendor")}</option>
              <option value="sku">Sort: SKU</option>
              <option value="lot_no">Sort: {t("lot")}</option>
              <option value="action">Sort: Action</option>
              <option value="type">Sort: {t("type")}</option>
            </select>
          </label>
          <label>
            <span>Order</span>
            <select value={order} onChange={(e) => setOrder(e.target.value)}>
              <option value="asc">Asc</option>
              <option value="desc">Desc</option>
            </select>
          </label>
          <label>
            <span>{t("type")}</span>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="">All {tp("type").toLowerCase()}</option>
              {[...new Set(rows.map((r) => r.type).filter(Boolean))].map(
                (t) => (
                  <option key={t}>{t}</option>
                ),
              )}
            </select>
          </label>
          <label>
            <span>Action</span>
            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
            >
              <option value="">All actions</option>
              {actionOptions.map((action) => (
                <option key={action} value={action}>
                  {action}
                </option>
              ))}
            </select>
          </label>
          <DateRangePicker
            value={dateDraft}
            onChange={setDateDraft}
            onApply={() => setDateRange({ ...dateDraft })}
            onClear={(empty) => setDateRange(empty)}
          />
          <div className="status-toolbar-actions">
            <button
              className="btn btn-primary"
              type="button"
              onClick={() => downloadExport("csv")}
            >
              Download CSV
            </button>
            <button
              className="btn btn-secondary"
              type="button"
              onClick={() => downloadExport("xlsx")}
            >
              Download Excel
            </button>
          </div>
        </div>
        {exportError ? <div className="banner error">{exportError}</div> : null}
        <div className="table--card" style={{ overflowX: "auto" }}>
          <table className="table table--fixed table--dense">
            <colgroup>
              <col />
              {/* Actor */}
              <col />
              {/* Vendor */}
              <col style={{ width: "28%" }} />
              {/* Material */}
              <col style={{ width: "14%" }} />
              {/* SKU */}
              <col style={{ width: "12%" }} />
              {/* Lot */}
              <col style={{ width: "16%" }} />
              {/* Action */}
              <col />
              {/* Time */}
              <col style={{ width: "9%" }} />
              {/* Type */}
            </colgroup>
            <thead>
              <tr>
                <th>Actor</th>
                <th>{t("vendor")}</th>
                <th>{t("material")}</th>
                <th>SKU</th>
                <th>{t("lot")}</th>
                <th>Action</th>
                <th>Time</th>
                <th>{t("type")}</th>
              </tr>
            </thead>
            <tbody>
              {pg.pageData.map((r, i) => (
                <tr key={i}>
                  <td>{displayActor(r.actor)}</td>
                  <td>{blankDash(r.vendor)}</td>
                  <td>{blankDash(r.material)}</td>
                  <td>{blankDash(r.sku)}</td>
                  <td>{blankDash(r.lot_no)}</td>
                  <td>
                    <span>{structuredValue(r.action)}</span>
                    {hasDetails(r.details) ? (
                      <button
                        className="icon-btn"
                        type="button"
                        aria-label="View audit details"
                        title="View audit details"
                        onClick={() => setDetailsRow(r)}
                        style={{ marginLeft: 8 }}
                      >
                        i
                      </button>
                    ) : null}
                  </td>
                  <td>{structuredValue(formatDateTime(r.time))}</td>
                  <td>{blankDash(r.type)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager {...pg} />
        </div>
      </div>
      <ModalShell
        open={!!detailsRow}
        title="Audit details"
        subtitle={detailsRow ? `${detailsRow.action || ""} · ${formatDateTime(detailsRow.time)}` : ""}
        size="lg"
        onClose={() => setDetailsRow(null)}
        actions={
          <button className="btn btn-primary" type="button" onClick={() => setDetailsRow(null)}>
            Close
          </button>
        }
      >
        {detailsRow ? (() => {
          const detail = splitDetails(detailsRow.details);
          const targets = targetEntries(detail.target);
          const changes = detail.changes || {};
          return (
            <div className="page-stack">
              {targets.length ? (
                <div className="section" style={{ padding: 12 }}>
                  <strong>Target</strong>
                  <div className="helper-text" style={{ marginTop: 6 }}>
                    {targets.map(([field, value]) => (
                      <span key={field} style={{ marginRight: 14 }}>
                        {field}: <strong>{formatDetailValue(value)}</strong>
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}
              <div className="table--card" style={{ overflowX: "auto", maxWidth: "100%" }}>
                <table className="table table--dense" style={{ tableLayout: "fixed", width: "100%" }}>
                  <thead>
                    <tr>
                      <th>Field</th>
                      <th>Before</th>
                      <th>After</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(changes).map(([field, value]) => {
                      const isChange = value && typeof value === "object" && ("from" in value || "to" in value);
                      return (
                        <tr key={field}>
                          <td style={detailCellStyle}>{field}</td>
                          <td style={detailCellStyle}>{isChange ? formatDetailValue(value.from) : formatDetailValue(value)}</td>
                          <td style={detailCellStyle}>{isChange ? formatDetailValue(value.to) : formatDetailValue(value)}</td>
                        </tr>
                      );
                    })}
                    {!Object.keys(changes).length ? (
                      <tr>
                        <td colSpan={3}>No field-level changes recorded.</td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })() : null}
      </ModalShell>
    </div>
  );
}
